/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

/**
 * This is an internal module.
 */

import type { EventType, IEvent } from '../@types/event';
import type { XHubChatEvent } from '../models/event/event';
import type { Room } from '../models/room/room';
import type { RoomSummary } from '../models/room/room-summary';
import type { User } from '../models/user/user';
import type { RoomData } from '../sync/interface';
import type { IStore, IStoredClientOpts, UserCreator } from './interfaces/store';

/**
 * Construct a stub store. This does no-ops on most store methods.
 */
export class StubStore implements IStore {
  public readonly accountData = new Map(); // stub
  private fromToken: string | null = null;

  /** @returns whether or not the database was newly created in this session. */
  public isNewlyCreated(): Promise<boolean> {
    return Promise.resolve(true);
  }

  /**
   * Get the sync token.
   */
  public getSyncToken(): string | null {
    return this.fromToken;
  }

  /**
   * Set the sync token.
   */
  public setSyncToken(token: string): void {
    this.fromToken = token;
  }

  /**
   * No-op.
   */
  public storeRoom(room: Room): void {}

  /**
   * No-op.
   */
  public getRoom(roomId: string): Room | null {
    return null;
  }

  /**
   * No-op.
   * @returns An empty array.
   */
  public getRooms(): Room[] {
    return [];
  }

  /**
   * Permanently delete a room.
   */
  public removeRoom(roomId: string): void {

  }

  /**
   * No-op.
   * @returns An empty array.
   */
  public getRoomSummaries(): RoomSummary[] {
    return [];
  }

  /**
   * No-op.
   */
  public storeUser(user: User): void {}

  /**
   * No-op.
   */
  public getUser(userId: string): User | null {
    return null;
  }

  /**
   * No-op.
   */
  public getUsers(): User[] {
    return [];
  }

  /**
   * No-op.
   */
  public scrollback(room: Room, limit: number): XHubChatEvent[] {
    return [];
  }

  /**
   * No-op.
   */
  public setUserCreator(creator: UserCreator): void {

  }

  /**
   * Store events for a room.
   * @param room - The room to store events for.
   * @param events - The events to store.
   * @param token - The token associated with these events.
   * @param toStart - True if these are paginated results.
   */
  public storeEvents(room: Room, events: XHubChatEvent[], token: string | null, toStart: boolean): void {}

  /**
   * Retrieve a filter ID with the given name.
   * @param filterName - The filter name.
   * @returns The filter ID or null.
   */
  public getFilterIdByName(filterName: string): string | null {
    return null;
  }

  /**
   * Set a filter name to ID mapping.
   */
  public setFilterIdByName(filterName: string, filterId?: string): void {}

  /**
   * Store user-scoped account data events
   * @param events - The events to store.
   */
  public storeAccountDataEvents(events: XHubChatEvent[]): void {}

  /**
   * Get account data event by event type
   * @param eventType - The event type being queried
   */
  public getAccountData(eventType: EventType | string): XHubChatEvent | undefined {
    return undefined;
  }

  /**
   * We never want to save because we have nothing to save to.
   *
   * @returns If the store wants to save
   */
  public wantsSave(): boolean {
    return false;
  }

  /**
   * Save does nothing as there is no backing data store.
   */
  public save(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Startup does nothing.
   * @returns An immediately resolved promise.
   */
  public startup(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * @returns If there is a saved sync, the nextBatch token
   * for this sync, otherwise null.
   */
  public getSavedSyncToken(): Promise<string | null> {
    return Promise.resolve(null);
  }

  /**
   * @returns Promise which resolves with a sync response to restore the
   * client state to where it was at the last save, or null if there
   * is no saved sync data.
   */
  public getSavedRooms(): Promise<RoomData[] | null> {
    return Promise.resolve(null);
  }

  /**
   * setRoomsData does nothing as there is no backing data store.
   * @param roomsData - The room data to set.
   * @returns An immediately resolved promise.
   */
  public setRoomsData(roomsData: Record<string, RoomData>): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Delete all data from this store. Does nothing since this store
   * doesn't store anything.
   * @returns An immediately resolved promise.
   */
  public deleteAllData(): Promise<void> {
    return Promise.resolve();
  }

  public getClientOptions(): Promise<IStoredClientOpts | undefined> {
    return Promise.resolve(undefined);
  }

  public storeClientOptions(options: IStoredClientOpts): Promise<void> {
    return Promise.resolve();
  }

  public async getPendingEvents(roomId: string): Promise<Partial<IEvent>[]> {
    return [];
  }

  public setPendingEvents(roomId: string, events: Partial<IEvent>[]): Promise<void> {
    return Promise.resolve();
  }

  public async removeToDeviceBatch(id: number): Promise<void> {
    return Promise.resolve();
  }

  public async destroy(): Promise<void> {
    // Nothing to do
  }
}
